import React, { useCallback, useEffect } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { Params } from 'react-router-dom'
import {
  couponDetailInfoTabQueryOption,
  couponDetailQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CouponDetailInfoTabSpace,
  CouponDetailResponseType,
  FavoriteStarSpace,
  HotDealCouponDetailSpace,
  PurchaseValidCheckSpace,
} from 'src/types/api'
import CouponDetailPageView from 'src/pages/CouponDetailPage/view'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import { Loading } from 'src/components/common/Loading'
import usePageNav from 'src/hooks/usePageNav.hook'
import {
  favoriteUpdateQueryOption,
  purchaseValidCheckQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { useModal } from 'src/provider/ModalContextProvider'
import { useToast } from 'src/provider/ToastContextProvider'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_PURCHASE_VALID_CHECK,
} from 'src/service/constant/errcodes'
import { animateScroll } from 'react-scroll'
import RenderIf from 'src/components/common/RenderIf'
import { handleJsonParse } from 'src/utils'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'
import { useCookies } from 'react-cookie'

export interface CouponDetailPageProps {
  purchaseValidCheckSubmit: (purchaseType: 'default' | 'gift') => void
  couponDetailData?: CouponDetailResponseType
  couponDetailTabData?: CouponDetailInfoTabSpace.Response
  favoriteUpdateHandler: (favoriteValue: 0 | 1) => void
}

export const couponDetailPageLoader =
  (queryClient: QueryClient) =>
  async ({
    params: { couponType, couponIdx, couponNumber },
  }: {
    params: Params<keyof MatchParams>
  }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(
        couponDetailQueryOption(couponType!, { CouponIdx: Number(couponIdx!) }),
      ),
      await queryClient.ensureQueryData(
        couponDetailInfoTabQueryOption({
          CouponType: Number(couponType!) as 0 | 1,
          CouponNumber: couponNumber!,
        }),
      ),
    ])
  }

const CouponDetailPage = () => {
  const { couponNumber, couponType, couponIdx } = useParamsType()
  const { purchasePageNav, giftPurchasePageNav, mainPageNav } = usePageNav()
  const { openToast } = useToast()
  const { openModal } = useModal()
  const [cookies, , removeCookie] = useCookies(['pg', 'orderId'])

  const {
    data: couponDetailQueryData,
    isLoading: isCouponDetailLoading,
    refetch: couponDetailRefetch,
  } = useCustomQuery<CouponDetailResponseType>(
    couponDetailQueryOption(couponType, { CouponIdx: Number(couponIdx) }),
  )

  const {
    data: couponDetailInfoTabQueryData,
    isLoading: isCouponDetailInfoLoading,
    isSuccess: isCouponDetailInfoSuccess,
  } = useCustomQuery<CouponDetailInfoTabSpace.Response>(
    couponDetailInfoTabQueryOption({
      CouponType: Number(couponType) as 0 | 1,
      CouponNumber: couponNumber,
    }),
  )

  const { mutateAsync: favoriteUpdateMutate } = useCustomMutation<
    FavoriteStarSpace.Request,
    FavoriteStarSpace.Response
  >(favoriteUpdateQueryOption())

  const { mutateAsync: purchaseValidCheckMutate } = useCustomMutation<
    PurchaseValidCheckSpace.Request,
    PurchaseValidCheckSpace.Response
  >(purchaseValidCheckQueryOption())

  const setCouponDetailInfoItems = useCallback(() => {
    if (!!couponDetailInfoTabQueryData?.Body) {
      const couponDetailInfoTabData = couponDetailInfoTabQueryData?.Body!
      const { Title, Contents } = couponDetailInfoTabData.CouponInfo!

      return {
        ...couponDetailInfoTabData,
        CouponInfo: {
          Title: Title,
          Contents: Contents?.replace('{{ValidDay}}', `${couponDetailQueryData?.Body?.ValidDay}일`),
        },
      }
    } else return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponDetailInfoSuccess, couponDetailInfoTabQueryData])

  const purchaseValidCheckSubmit = (purchaseType: 'default' | 'gift') => {
    const submitData = {
      CouponIdx: Number(couponIdx),
      CouponType: couponDetailQueryData?.Body?.CouponType as 0 | 1 | 2,
      SalePrice: couponDetailQueryData?.Body?.SalePrice as number,
    }
    addFlowBreadcrumb('payment_attempt', 'request', {
      handler: 'purchaseValidCheckSubmit',
      type: purchaseType,
    })
    purchaseValidCheckMutate(submitData).then(({ Header, Body }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        purchaseType === 'default'
          ? purchasePageNav(Number(couponType), Number(couponIdx), couponNumber)
          : giftPurchasePageNav(Number(couponType), Number(couponIdx), couponNumber)
      } else {
        addFlowBreadcrumb('payment_fail', 'response', {
          handler: 'purchaseValidCheckSubmit',
          errCode: Header.ErrCode,
          type: purchaseType,
        })
        const ErrMsg = handleJsonParse(Header.ErrMsg)
        const { Title = '', Contents = '' } = ErrMsg
        switch (Header.ErrCode) {
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_NOT_VALID_COUPON:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_HOT_DEAL_SOLD_OUT:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_ONLY_ONE_HOTDEAL_PURCHASE:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DAILY_PURCHASED:
            openModal({ contents: { t: Title, d: Contents }, button: { submit: { t: '확인' } } })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DIFFERENT_PRICE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '확인', f: () => window.location.reload() } },
            })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_MAX_OF_MONTH_HOTDEAL_PURCHASE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '닫기' } },
            })
            break
          //통신 모듈에 있는 에러 핸들링 사용
          case ERRCODES_COMMON.PIN_CHECK_ERROR:
          case ERRCODES_COMMON.SESSION_ERROR:
          case ERRCODES_COMMON.ERROR_LOGOUT:
            break
          default:
            openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
            break
        }
      }
    })
  }

  const favoriteUpdateHandler = (favoriteValue: 0 | 1) => {
    const register = favoriteValue === 0 // 현재 미등록 상태

    const favoriteUpdate = () => {
      favoriteUpdateMutate({
        CouponNumber: couponNumber,
        CouponIdx: Number(couponIdx),
        Type: favoriteValue,
      }).then(({ Header }) => {
        if (Header.ErrCode === ERRCODE_SUCCESS) {
          couponDetailRefetch?.()

          const contents = register
            ? '해당 쿠폰을 즐겨찾기로 등록 하였습니다.'
            : '즐겨찾기에서 삭제하였습니다.'

          openToast({ contents })
        }
      })
    }

    if (register) {
      openModal({
        contents: {
          t: '즐겨찾기 등록',
          d: '즐겨찾기에 등록하면 쿠폰지갑에서 쉽게 관리 및 추가구매가 가능합니다.',
        },
        button: { submit: { t: '등록', f: favoriteUpdate }, cancel: { t: '취소' } },
      })
    } else favoriteUpdate()
  }

  //사용자 임의조작 접근
  useEffect(() => {
    //핫딜
    const hotDealItems = couponType === '1'
    // 0:핫딜 진행중, 1:핫딜예정, 2:품절
    const incomingCoupons =
      (couponDetailQueryData?.Body as HotDealCouponDetailSpace.Response)?.SellFlag !== 0
    if (hotDealItems && incomingCoupons) {
      mainPageNav()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponType])

  useEffect(() => {
    animateScroll.scrollToTop({ smooth: false })
    //결제중 비정상 종료시 남은 데이터 정리
    if (cookies['orderId'] !== undefined) {
      removeCookie('pg', { path: '/' })
      removeCookie('orderId', { path: '/' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const props: CouponDetailPageProps = {
    couponDetailData: couponDetailQueryData?.Body,
    couponDetailTabData: setCouponDetailInfoItems(),
    purchaseValidCheckSubmit,
    favoriteUpdateHandler,
  }

  return (
    <React.Fragment>
      <RenderIf when={isCouponDetailLoading || isCouponDetailInfoLoading}>
        <Loading.Default />
      </RenderIf>
      <CouponDetailPageView {...props} />
    </React.Fragment>
  )
}
export default CouponDetailPage
